import React, { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/react-hooks'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupAddon,
  Table,
  Badge,
  Spinner,
  Alert,
  CustomInput,
} from '@/components/ui-compat'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import {
  faShareAlt,
  faLink,
  faCopy,
  faPlus,
  faTrash,
  faClock,
  faLock,
  faUnlock,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import DatePicker from 'react-datepicker'
import * as lngs from 'date-fns/locale'
import i18n from '../../../i18n'
import 'react-datepicker/dist/react-datepicker.css'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { formatDateTime } from '../../../utils/formatters'

interface ShareLinkModalProps {
  isOpen: boolean
  onClose: (changed?: boolean) => void
  folderId?: string
  documentId?: string
}

interface ShareLink {
  id: string
  token: string
  hasPassword: boolean
  expiresAt: string | null
  accessCount: number
  lastAccessedAt: string | null
  createdAt: string
  url: string
  isExpired: boolean
  createdBy: {
    id: string
    name: string
  }
}

const ShareLinkModal: React.FC<ShareLinkModalProps> = ({
  isOpen,
  onClose,
  folderId,
  documentId,
}) => {
  const { t } = useTranslation()
  const [hasChanges, setHasChanges] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [expiresAt, setExpiresAt] = useState<Date | null>(null)
  const [password, setPassword] = useState('')
  const [usePassword, setUsePassword] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Fetch share links
  const { data, loading, refetch } = useQuery(Query.DOC_SHARE_LINKS, {
    variables: { folderId, documentId },
    fetchPolicy: 'network-only',
  })

  // Create share link mutation
  const [createShareLink, { loading: creating }] = useMutation(Mutation.CREATE_DOC_SHARE_LINK, {
    onCompleted: (data) => {
      toastr.success('', t('DocControl_share_link_created'))
      setShowCreateForm(false)
      setExpiresAt(null)
      setPassword('')
      setUsePassword(false)
      setHasChanges(true)
      refetch()

      // Auto-copy the new link
      if (data?.createDocShareLink?.url) {
        navigator.clipboard.writeText(data.createDocShareLink.url)
        toastr.info('', t('DocControl_link_copied'))
      }
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  // Revoke share link mutation
  const [revokeShareLink] = useMutation(Mutation.REVOKE_DOC_SHARE_LINK, {
    onCompleted: () => {
      toastr.success('', t('DocControl_share_link_revoked'))
      setHasChanges(true)
      refetch()
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  const shareLinks: ShareLink[] = data?.docShareLinks || []
  const activeLinks = shareLinks.filter((link) => !link.isExpired)
  const expiredLinks = shareLinks.filter((link) => link.isExpired)

  const handleCreate = async () => {
    if (!folderId && !documentId) {
      toastr.error(t('Common_error'), 'No folder or document specified')
      return
    }
    await createShareLink({
      variables: {
        folderId: folderId || null,
        documentId: documentId || null,
        expiresAt: expiresAt ? expiresAt.toISOString() : null,
        password: usePassword && password ? password : null,
      },
    })
  }

  const handleRevoke = async (linkId: string) => {
    if (!window.confirm(t('DocControl_revoke_link_confirm'))) return
    await revokeShareLink({ variables: { id: linkId } })
  }

  const handleCopyLink = (link: ShareLink) => {
    navigator.clipboard.writeText(link.url)
    setCopiedId(link.id)
    toastr.success('', t('DocControl_link_copied'))
    setTimeout(() => setCopiedId(null), 2000)
  }

  // Calculate min date for expiration (tomorrow)
  const minDate = new Date()
  minDate.setDate(minDate.getDate() + 1)

  return (
    <Modal isOpen={isOpen} toggle={() => onClose(hasChanges)} size="lg" centered>
      <ModalHeader toggle={() => onClose(hasChanges)}>
        <Icon icon={faShareAlt as any} className="mr-2 text-primary" />
        {t('DocControl_share_links')}
      </ModalHeader>

      <ModalBody>
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            {/* Create New Link Button/Form */}
            {!showCreateForm ? (
              <div className="mb-4">
                <Button
                  color="primary"
                  onClick={() => setShowCreateForm(true)}
                >
                  <Icon icon={faPlus as any} className="mr-2" />
                  {t('DocControl_create_share_link')}
                </Button>
              </div>
            ) : (
              <div className="p-3 bg-muted rounded mb-4 border">
                <h6 className="mb-3 font-bold">{t('DocControl_new_share_link')}</h6>

                <FormGroup className="mb-3">
                  <Label className="mb-1">{t('DocControl_expiration_date')}</Label>
                  <DatePicker
                    selected={expiresAt}
                    onChange={(date: Date | null) => setExpiresAt(date)}
                    dateFormat="yyyy/MM/dd"
                    className="form-control"
                    locale={(lngs as any)[i18n.language] || (lngs as any).en}
                    minDate={minDate}
                    isClearable
                    placeholderText={t('DocControl_expiration_placeholder')}
                  />
                  <small className="text-muted-foreground block mt-1">{t('DocControl_expiration_help')}</small>
                </FormGroup>

                <FormGroup className="mb-3">
                  <CustomInput
                    type="switch"
                    id="usePassword"
                    label={t('DocControl_protect_with_password')}
                    checked={usePassword}
                    onChange={(e) => setUsePassword(e.target.checked)}
                  />
                </FormGroup>

                {usePassword && (
                  <FormGroup className="mb-3">
                    <Label className="mb-1">{t('DocControl_password')}</Label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('DocControl_password_placeholder')}
                    />
                  </FormGroup>
                )}

                <div className="flex">
                  <Button
                    color="secondary"
                    className="mr-2"
                    onClick={() => {
                      setShowCreateForm(false)
                      setExpiresAt(null)
                      setPassword('')
                      setUsePassword(false)
                    }}
                  >
                    {t('Common_cancel')}
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleCreate}
                    disabled={creating || (usePassword && !password)}
                  >
                    {creating ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <Icon icon={faLink as any} className="mr-1" />
                        {t('DocControl_create_link')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Active Links */}
            <h6 className="mb-3 font-bold">
              {t('DocControl_active_links')}
              <Badge color="primary" pill className="ml-2">{activeLinks.length}</Badge>
            </h6>
            {activeLinks.length === 0 ? (
              <Alert color="light" className="text-muted-foreground border mb-4 py-3 px-3">
                {t('DocControl_no_active_links')}
              </Alert>
            ) : (
              <Table responsive size="sm" className="mb-4">
                <thead className="bg-muted">
                  <tr>
                    <th className="py-2">{t('DocControl_link')}</th>
                    <th className="py-2">{t('DocControl_security')}</th>
                    <th className="py-2">{t('DocControl_expires')}</th>
                    <th className="py-2">{t('DocControl_accesses')}</th>
                    <th className="py-2 text-right">{t('Common_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {activeLinks.map((link) => (
                    <tr key={link.id}>
                      <td className="py-2 align-middle">
                        <InputGroup size="sm">
                          <Input
                            type="text"
                            value={link.url}
                            readOnly
                            className="bg-white"
                            style={{ fontSize: '0.75rem' }}
                          />
                          <InputGroupAddon addonType="append">
                            <Button
                              color={copiedId === link.id ? 'success' : 'outline-secondary'}
                              onClick={() => handleCopyLink(link)}
                              title={t('DocControl_copy_link')}
                            >
                              <Icon icon={(copiedId === link.id ? faCheckCircle : faCopy) as any} />
                            </Button>
                          </InputGroupAddon>
                        </InputGroup>
                      </td>
                      <td className="py-2 align-middle">
                        {link.hasPassword ? (
                          <Badge color="success">
                            <Icon icon={faLock as any} className="mr-1" />
                            {t('DocControl_protected')}
                          </Badge>
                        ) : (
                          <Badge color="light" className="text-muted-foreground border">
                            <Icon icon={faUnlock as any} className="mr-1" />
                            {t('DocControl_public')}
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 align-middle">
                        {link.expiresAt ? (
                          <small>
                            <Icon icon={faClock as any} className="mr-1 text-muted-foreground" />
                            {formatDateTime(link.expiresAt)}
                          </small>
                        ) : (
                          <Badge color="light" className="text-muted-foreground">{t('DocControl_never')}</Badge>
                        )}
                      </td>
                      <td className="py-2 align-middle">
                        <Badge color="info" pill>
                          {link.accessCount}
                        </Badge>
                        {link.lastAccessedAt && (
                          <small className="text-muted-foreground block mt-1">
                            {formatDateTime(link.lastAccessedAt)}
                          </small>
                        )}
                      </td>
                      <td className="py-2 align-middle text-right">
                        <Button
                          color="link"
                          size="sm"
                          className="text-destructive p-1"
                          onClick={() => handleRevoke(link.id)}
                          title={t('DocControl_revoke_link')}
                        >
                          <Icon icon={faTrash as any} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {/* Expired Links */}
            {expiredLinks.length > 0 && (
              <div className="mt-4 pt-3 border-t">
                <h6 className="mb-3 text-muted-foreground font-bold">
                  {t('DocControl_expired_links')}
                  <Badge color="secondary" pill className="ml-2">{expiredLinks.length}</Badge>
                </h6>
                <Table responsive size="sm" className="text-muted-foreground mb-0">
                  <tbody>
                    {expiredLinks.map((link) => (
                      <tr key={link.id}>
                        <td className="py-2">
                          <small>{link.url.substring(0, 50)}...</small>
                        </td>
                        <td className="py-2">
                          <Badge color="danger">{t('DocControl_expired')}</Badge>
                        </td>
                        <td className="py-2">
                          <small>{t('DocControl_accessed_times', { count: link.accessCount })}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={() => onClose(hasChanges)}>
          {t('Common_close')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ShareLinkModal
